import { useTimelineStore } from '@/stores/api/timeline';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { render } from '@/threejs/core';
import Hls from 'hls.js';
import * as THREE from 'three';
class TextureManager {
  constructor() {
    this.videoElements = new Map(); // Map of video URLs to video elements
    this.imageTextures = new Map(); // Map of image URLs to textures
    this.videoTextures = new Map(); // Map of video URLs to video textures
    this.hlsInstances = new Map(); // Map of HLS URLs to HLS.js instances
  }

  getVideoElement(url) {
    if (!url) return null;
    if (!this.videoElements.has(url)) {
      const video = document.createElement('video');
      video.muted = true;
      video.loop = false;
      video.crossOrigin = 'anonymous';
      // Check if it's an HLS stream
      if (url.includes('.m3u8') || url.includes('hls')) {
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: false,
            lowLatencyMode: true,
            backBufferLength: 90,
            xhrSetup: function (xhr, url) {
              // Handle CORS and auth headers if needed
              xhr.withCredentials = false;
            },
          });

          hls.loadSource(url);
          hls.attachMedia(video);

          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS error:', data);
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  hls.recoverMediaError();
                  break;
                default:
                  hls.destroy();
                  break;
              }
            }
          });

          this.hlsInstances.set(url, hls);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Fallback for browsers that support HLS natively (Safari)
          video.src = url;
        } else {
          console.error('HLS not supported in this browser');
          return null;
        }
      } else {
        // Regular video file
        video.src = url;
      }

      this.videoElements.set(url, video);
    }
    return this.videoElements.get(url);
  }

  getVideoTexture(url) {
    if (!this.videoTextures.has(url)) {
      const video = this.getVideoElement(url);
      const texture = new THREE.VideoTexture(video);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.format = THREE.RGBAFormat;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      this.videoTextures.set(url, texture);
    }
    return this.videoTextures.get(url);
  }

  async getImageTexture(url) {
    if (!this.imageTextures.has(url)) {
      return new Promise((resolve, reject) => {
        new THREE.TextureLoader().load(
          url,
          (loadedTexture) => {
            loadedTexture.wrapS = THREE.ClampToEdgeWrapping;
            loadedTexture.wrapT = THREE.ClampToEdgeWrapping;
            loadedTexture.minFilter = THREE.LinearFilter;
            loadedTexture.format = THREE.RGBAFormat;
            loadedTexture.colorSpace = THREE.SRGBColorSpace;
            loadedTexture.magFilter = THREE.LinearFilter;
            this.imageTextures.set(url, loadedTexture);
            resolve(loadedTexture);
          },
          undefined,
          () => {
            reject(new Error(`Failed to load texture: ${url}`));
          }
        );
      });
    } else {
      return this.imageTextures.get(url);
    }
  }

  playVideo(url) {
    const video = this.getVideoElement(url);

    if (video.paused) {
      video
        .play()
        .then(() => console.log('Video play successful'))
        .catch((e) => console.error('Error playing video:', e));
    } else {
      console.log('Video is already playing');
    }
  }

  pauseVideo(url) {
    const video = this.getVideoElement(url);
    if (!video.paused) {
      video.pause();
    }
  }

  pauseAllVideos() {
    const timelineStore = useTimelineStore();
    const timelinePlayerStore = useTimelinePlayerStore();
    this.videoElements.forEach((video) => {
      video.pause();
    });
    timelinePlayerStore.pause();
  }

  playAllVideos() {
    const timelineStore = useTimelineStore();

    const timelinePlayerStore = useTimelinePlayerStore();
    this.videoElements.forEach((video, url) => {
      if (!video.src && !video.querySelector('source')) return;
      video.play().catch((error) => {
        console.warn(`Failed to play video (${url}):`, error.message);
      });
    });
    timelinePlayerStore.play();
  }

  seekVideo(url, time) {
    const video = this.getVideoElement(url);
    if (video) {
      video.currentTime = time;
    }
  }

  seekAllVideos(time) {
    this.videoElements.forEach((video) => {
      video.currentTime = time;
    });
    render();
  }

  // Call this when timeline is clicked or dragged
  seekToTime(time) {
    this.seekAllVideos(time);
  }

  releaseVideo(url) {
    const hls = this.hlsInstances.get(url);
    if (hls) {
      hls.destroy();
      this.hlsInstances.delete(url);
    }
    const video = this.videoElements.get(url);
    if (video) {
      video.pause();
      video.src = '';
      this.videoElements.delete(url);
    }
    const texture = this.videoTextures.get(url);
    if (texture) {
      texture.dispose();
      this.videoTextures.delete(url);
    }
  }

  releaseImage(url) {
    const texture = this.imageTextures.get(url);
    if (texture) {
      texture.dispose();
      this.imageTextures.delete(url);
    }
  }

  dispose() {
    // Clean up HLS instances
    this.hlsInstances.forEach((hls) => {
      hls.destroy();
    });
    this.hlsInstances.clear();

    // Clean up video elements
    this.videoElements.forEach((video) => {
      video.pause();
      video.src = '';
    });
    this.videoElements.clear();

    // Clean up textures
    this.videoTextures.forEach((texture) => texture.dispose());
    this.videoTextures.clear();

    this.imageTextures.forEach((texture) => texture.dispose());
    this.imageTextures.clear();
  }
}

// Export a singleton instance
export const textureManager = new TextureManager();
